// src/modules/app-views/goodsReceipt/pages/Init/modals/NfeImportModal/Step2.jsx
// *******************************************************************************
// NFE IMPORT MODAL - STEP 2: PROCESSING & RESULT
// *******************************************************************************
// Shows processing spinner while backend processes the file, then displays
// success result, duplicate warning, or error message.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Spin, Result, Typography, Descriptions } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';

const { Text } = Typography;

// *******************************************************************************
// STEP FUNCTION
// *******************************************************************************
const step2Processing = ({ importState }) => ({
  stepTitle: translateX('processing_document'),
  stepTooltip: translateX('processing_document'),
  content: <Step2Content importState={importState} />,
});

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step2Content = ({ importState }) => {
  const { status, data, error } = importState || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────

  // Error state
  if (status === 'error') {
    return (
      <Result
        status="error"
        title={translateX('import_error')}
        subTitle={error || translateX('unexpected_error')}
      />
    );
  }

  // Duplicate — confirmed (cannot overwrite)
  if (data?.duplicate && !data?.canOverwrite) {
    return (
      <Result
        status="warning"
        icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
        title={translateX('duplicate_confirmed_title')}
        subTitle={translateX('duplicate_confirmed_message')}
      />
    );
  }

  // Duplicate — draft (can overwrite)
  if (data?.duplicate && data?.canOverwrite) {
    return (
      <Result
        status="warning"
        icon={<WarningOutlined />}
        title={translateX('duplicate_draft_title')}
        subTitle={translateX('duplicate_draft_message')}
      />
    );
  }

  // Success
  if (status === 'success' && data) {
    return (
      <Result
        status="success"
        icon={<CheckCircleOutlined />}
        title={translateX('document_imported_successfully')}
        subTitle={`${translateX('document')} #${data.documentNumber}`}
      >
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label={translateX('supplier')}>
            {data.issuerName || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('items')}>
            {data.itemsCount || 0}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('total_value')}>
            {data.totalAmount
              ? financialService.formatCurrency(data.totalAmount)
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('origin')}>
            {data.origin === 'xml_import' ? 'XML' : 'IA'}
          </Descriptions.Item>
        </Descriptions>
      </Result>
    );
  }

  // Loading/idle — show spinner (default state when entering step 2)
  return (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      <div style={{ marginTop: 16 }}>
        <Text type="secondary">{translateX('processing_fiscal_document')}</Text>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default step2Processing;
